import React, { useEffect } from 'react';
import {
  BrowserRouter as Router,
  Route,
  Switch,
  Redirect,
} from 'react-router-dom';
import NavBar from './components/navbar/NavBar';
import Home from './views/UserViews/home/Home';
import AppInbox from './views/SafeguardViews/app_inbox/AppInbox';
import CompassErrorLog from './views/SafeguardViews/compass_error_log/CompassErrorLog';
import OrganizationManagement from './views/UserViews/organization_management/OrganizationManagement';
import Profile from './views/UserViews/profile/Profile';
import UserManagement from './views/UserViews/user_management/UserManagement';
import DeviceManagement from './views/UserViews/device_management/DeviceManagement';
import EmergencyResponseManagement from './views/UserViews/emergency_responder_management/EmergencyResponderManagement';
import CompassConfigurations from './views/UserViews/compass_configurations/CompassConfigurationsPage';
import OrganizationDetails from './views/SafeguardViews/organization_details/OrganizationDetails';
import { useAuth0 } from '@auth0/auth0-react';
import './App.css';
import { UserProvider } from './context_providers/user_context/UserProvider';
import { OrganizationProvider } from './context_providers/organization_context/OrganizationProvider';
import { SignalRListener } from './context_providers/signal_r_context/SignalRListener';
import { UserUpdaterProvider } from './context_providers/user_context/UserUpdaterProvider';
import { EmergencyUpdaterProvider } from './context_providers/emergency_context/EmergencyUpdaterProvider';
import { EmergencyProvider } from './context_providers/emergency_context/EmergencyProvider';
import { HandleAudioProvider } from './context_providers/audio_context/HandleAudioProvider';
import EmergencyHistory from './views/UserViews/emergency_history/EmergencyHistory';
import SignUp from './views/UserViews/sign_up/SignUp';
import FirmwareVersioning from './views/SafeguardViews/firmware_versioning/FirmwareVersioning';
import ErrorComponent from './components/utils/ErrorComponent';
import { LoadingNavBar } from './components/navbar/LoadingNavBar';
import DevelopmentEnvironmentBanner from './components/banners/dev_banners/DevelopmentEnvironmentBanner';
import AppVersioning from './views/SafeguardViews/app_versioning/AppVersioning';
import { ToastProvider } from './context_providers/toast_context/ToastContext';
import { ScreenSizeProvider } from './context_providers/screen_context/ScreenSizeProvider';
import ProtectedSafeguardRoute from './components/routing/SafeguardRoutes/ProtectedSafeguardRoute';
import ProtectedUserRoute from './components/routing/UserRoutes/ProtectedUserRoute';
import ProtectedUserAdminRoute from './components/routing/UserRoutes/ProtectedUserAdminRoute';
import { SafeguardRoles } from './components/routing/SafeguardRoutes/AcceptedSafeguardRoles';
import { BannerHeightProvider } from './context_providers/banner_context/BannerHeightContext';
import SupportLinks from './views/SafeguardViews/support_links/SupportLinks';
import DFUFileManagement from './views/SafeguardViews/dfu_file_management/DFUFileManagement';
import PrototypeFirmwareManagement from './views/SafeguardViews/prototype_firmware/PrototypeFirmwareManagement';
import { LoadingProvider } from './context_providers/loading_context/LoadingContext';
import LoadingIndicator from './context_providers/loading_context/LoadingIndicator';
import { EmergencyChatProvider } from './context_providers/emergency_chat_context/EmergencyChatProvider';
import Manufacturing from './views/SafeguardViews/manufacturing/Manufacturing';
import Analytics from './views/SafeguardViews/analytics/Analytics';
import { Routes } from './enums/Routes';
import { AppVersionProvider } from './context_providers/app_version_context/AppVersionProvider';
import { AppVersionUpdaterProvider } from './context_providers/app_version_context/AppVersionUpdaterProvider';
import MaintenanceIndicator from './context_providers/app_version_context/MaintenanceIndicator';
import CookieBanner from './components/banners/cookie_banner/CookieBanner';
import { ConnectivityProvider } from './context_providers/connectivity_context/ConnectivityProvider';
import { SafeguardAdminSignalRListener } from './context_providers/signal_r_context/SafeguardAdminSignalRListener';
import Auth0ErrorCodes from './utils/error_codes/Auth0ErrorCodes';
import Help from './views/UserViews/help/Help';
import CompassCore from './views/SafeguardViews/compass_core/CompassCore';
import { ThemeProvider } from '@mui/material';
import { theme } from './resources/style/theme';
import Insights from './views/UserViews/insights/Insights';

const App = () => {
  const { error, isLoading, isAuthenticated, loginWithRedirect } = useAuth0();

  useEffect(() => {
    if (!isLoading && !isAuthenticated) {
      loginWithRedirect();
    }
  }, [isLoading, isAuthenticated, loginWithRedirect, error]);

  if (error) {
    return (
      <>
        <LoadingNavBar />
        <ErrorComponent error={Auth0ErrorCodes.AUTH_0_GENERIC_ERROR} />
      </>
    );
  }

  if (isLoading || !isAuthenticated) {
    return (
      <>
        <LoadingNavBar />
      </>
    );
  }

  return (
    <div id="app" className="h-100">
      <Router>
        <ThemeProvider theme={theme}>
        <LoadingProvider>
          <ConnectivityProvider>
            <ScreenSizeProvider>
              <BannerHeightProvider>
                <ToastProvider>
                  <UserProvider>
                    <UserUpdaterProvider>
                      <OrganizationProvider>
                        <EmergencyChatProvider>
                          <EmergencyUpdaterProvider>
                            <AppVersionUpdaterProvider>
                              <AppVersionProvider>
                                <SignalRListener>
                                  <EmergencyProvider>
                                    <HandleAudioProvider>
                                      <SafeguardAdminSignalRListener>
                                          <NavBar />
                                          <DevelopmentEnvironmentBanner />
                                          <MaintenanceIndicator />
                                          <LoadingIndicator />
                                          <CookieBanner />
                                          <Switch>
                                            <Route
                                              path={Routes.SIGN_UP}
                                              component={SignUp}
                                            />
                                            <ProtectedUserRoute
                                              path={Routes.HOME}
                                              exact
                                              component={Home}
                                            />
                                            <ProtectedUserRoute
                                              path={
                                                Routes.COMPASS_CONFIGURATIONS
                                              }
                                              component={CompassConfigurations}
                                            />
                                            <ProtectedUserRoute
                                              path={Routes.PROFILE}
                                              component={Profile}
                                            />
                                            <ProtectedUserRoute
                                              path={Routes.HELP}
                                              component={Help}
                                            />
                                            <ProtectedUserRoute
                                              path={Routes.EMERGENCY_HISTORY}
                                              component={EmergencyHistory}
                                            />
                                            <ProtectedUserAdminRoute
                                              path={Routes.USER_MANAGEMENT}
                                              component={UserManagement}
                                            />
                                            <ProtectedUserAdminRoute
                                              path={
                                                Routes.EMERGENCY_RESPONDER_MANAGEMENT
                                              }
                                              component={
                                                EmergencyResponseManagement
                                              }
                                            />
                                            <ProtectedUserAdminRoute
                                              path={
                                                Routes.ORGANIZATION_MANAGEMENT
                                              }
                                              component={OrganizationManagement}
                                            />
                                            <ProtectedUserAdminRoute
                                              path={Routes.DEVICE_MANAGEMENT}
                                              component={DeviceManagement}
                                            />
                                            <ProtectedUserAdminRoute
                                              path={Routes.INSIGHTS}
                                              component={Insights}
                                            />
                                            <ProtectedSafeguardRoute
                                              path={Routes.APP_INBOX}
                                              component={AppInbox}
                                              requiredPermission={
                                                SafeguardRoles.SAFEGUARD_ADMIN_WRITE
                                              }
                                            />
                                            <ProtectedSafeguardRoute
                                              path={Routes.COMPASS_ERROR_LOG}
                                              component={CompassErrorLog}
                                              requiredPermission={
                                                SafeguardRoles.SAFEGUARD_ADMIN_WRITE
                                              }
                                            />
                                            <ProtectedSafeguardRoute
                                              path={Routes.SUPPORT_LINKS}
                                              component={SupportLinks}
                                              requiredPermission={
                                                SafeguardRoles.SAFEGUARD_ADMIN_WRITE
                                              }
                                            />
                                            <ProtectedSafeguardRoute
                                              path={Routes.DFU_FILE_MANAGEMENT}
                                              component={DFUFileManagement}
                                              requiredPermission={
                                                SafeguardRoles.SAFEGUARD_ADMIN_WRITE
                                              }
                                            />
                                            <ProtectedSafeguardRoute
                                              path={Routes.PROTOTYPE_FIRMWARE}
                                              component={
                                                PrototypeFirmwareManagement
                                              }
                                              requiredPermission={
                                                SafeguardRoles.SAFEGUARD_ADMIN_WRITE
                                              }
                                            />
                                            <ProtectedSafeguardRoute
                                              path={Routes.DEVICE_FIRMWARE}
                                              component={FirmwareVersioning}
                                              requiredPermission={
                                                SafeguardRoles.SAFEGUARD_ADMIN_WRITE
                                              }
                                            />
                                            <ProtectedSafeguardRoute
                                              path={Routes.ORGANIZATION}
                                              component={OrganizationDetails}
                                              requiredPermission={
                                                SafeguardRoles.SAFEGUARD_ADMIN_WRITE
                                              }
                                            />
                                            <ProtectedSafeguardRoute
                                              path={Routes.COMPASS_CORE}
                                              component={CompassCore}
                                              requiredPermission={
                                                SafeguardRoles.SAFEGUARD_ADMIN_WRITE
                                              }
                                            />
                                            <ProtectedSafeguardRoute
                                              path={Routes.APP_VERSIONING}
                                              component={AppVersioning}
                                              requiredPermission={
                                                SafeguardRoles.SAFEGUARD_ADMIN_WRITE
                                              }
                                            />
                                            <ProtectedSafeguardRoute
                                              path={Routes.MANUFACTURING}
                                              component={Manufacturing}
                                              requiredPermission={
                                                SafeguardRoles.SAFEGUARD_ADMIN_WRITE
                                              }
                                            />
                                            <ProtectedSafeguardRoute
                                              path={Routes.ANALYTICS}
                                              component={Analytics}
                                              requiredPermission={
                                                SafeguardRoles.SAFEGUARD_ADMIN_WRITE
                                              }
                                            />
                                            <Route
                                              render={() => (
                                                <Redirect to={Routes.HOME} />
                                              )}
                                            />
                                          </Switch>
                                      </SafeguardAdminSignalRListener>
                                    </HandleAudioProvider>
                                  </EmergencyProvider>
                                </SignalRListener>
                              </AppVersionProvider>
                            </AppVersionUpdaterProvider>
                          </EmergencyUpdaterProvider>
                        </EmergencyChatProvider>
                      </OrganizationProvider>
                    </UserUpdaterProvider>
                  </UserProvider>
                </ToastProvider>
              </BannerHeightProvider>
            </ScreenSizeProvider>
          </ConnectivityProvider>
        </LoadingProvider>
        </ThemeProvider>
      </Router>
    </div>
  );
};

export default App;
